import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogContent
} from '@mui/material';
import { Menu as MenuIcon, Settings, Logout, OndemandVideoOutlined, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// The video is served as a static file by the backend itself (backend/public/videos),
// not through the FTP-uploaded-image pipeline the adverts use - so its URL is the
// API host with the trailing /api dropped, not a value services/api.js already builds.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const MEDIA_BASE_URL = API_URL.replace(/\/api\/?$/, '');

const Navbar = ({ onMenuClick }) => {
  const { user, selectedOrganisation, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/administrators');
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
        height: '64px'
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', height: '64px' }}>
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography 
          variant="h6" 
          noWrap 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1.1rem' }
          }}
        >
          {selectedOrganisation?.name || 'UNFTP Organisation Interface'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="How to use UNFTP">
            <IconButton color="inherit" onClick={() => setVideoOpen(true)} size="small">
              <OndemandVideoOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <span>
              <IconButton 
                color="inherit" 
                onClick={handleMenuOpen}
                size="small"
                sx={{ ml: 1 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: '0.9rem'
                  }}
                >
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { 
              mt: 1.5,
              minWidth: 220,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid #e2e8f0'
            }
          }}
        >
          <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedOrganisation?.name || 'No organisation'}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleSettings}
            sx={{
              borderRadius: 1,
              mx: 1,
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
            }}
          >
            <Settings fontSize="small" sx={{ mr: 2 }} />
            Settings
          </MenuItem>
          <MenuItem 
            onClick={handleLogout} 
            sx={{ 
              borderRadius: 1, 
              mx: 1,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              }
            }}
          >
            <Logout fontSize="small" sx={{ mr: 2, color: 'error.main' }} />
            <Typography color="error">Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>

      <Dialog open={videoOpen} onClose={() => setVideoOpen(false)} maxWidth="sm" fullWidth>
        <IconButton
          onClick={() => setVideoOpen(false)}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 1, bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
        >
          <Close fontSize="small" />
        </IconButton>
        <DialogContent sx={{ p: 0, bgcolor: '#000', lineHeight: 0 }}>
          {videoOpen && (
            <Box
              component="video"
              src={`${MEDIA_BASE_URL}/videos/unftp.mp4`}
              controls
              autoPlay
              sx={{ width: '100%', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;